import { Button, CustomSelect, ErrorForm, InputForm } from '@components/index'
import { IOptions } from '@components/inputs/CustomSelect'
import { useYupValidation } from '@hooks/index'
import { IRealEstateSummery } from '@interfaces/realEstates/iRealEstate'
import { ICreateTableSales, ITableSales, ITemplate } from '@interfaces/tables/tableSales'
import { LocalizationProvider, MobileDatePicker, ptBR } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { salesTableSchema } from '@yupSchemas/index'
import ptBr from 'dayjs/locale/pt-br'
import React from 'react'
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { Params, useNavigate } from 'react-router-dom'
import InputTemplate from './InputTemplate'
import TableSaleBody from './TableSaleBody'
import TableSaleHeader from './TableSaleHeader'

interface IProps {
    submit: (values: ICreateTableSales) => void
    realEstates: IRealEstateSummery[]
    data?: ITableSales
    pageThatWas?: string
    getTemplate: () => Promise<ITemplate | undefined>
    params?: Readonly<Params<string>>
}

export default function TableSales(props: IProps): React.ReactElement {
    const navigate = useNavigate()
    const methods = useForm<ICreateTableSales>({
        resolver: useYupValidation(salesTableSchema),
    })
    const [realEstates, setRealEstates] = React.useState<IOptions[]>([])

    const control = methods.control
    const {
        fields: fieldsColumns,
        append: appendColumns,
        remove: removeColumns,
    } = useFieldArray<ICreateTableSales>({
        control,
        name: 'columns',
    })

    const {
        fields: fieldsRows,
        append: appendRows,
        remove: removeRows,
        update: updateRows
    } = useFieldArray({
        control,
        name: 'rows'
    })

    React.useEffect(() => {
        setRealEstates(props.realEstates.map((realEstate) => (
            {
                title: realEstate.name,
                value: realEstate.id.toString()
            }
        )))
    }, [props.realEstates])

    React.useEffect(() => {
        if (props.data)
            methods.reset({
                realEstateId: props.data.realEstateId ? props.data.realEstateId.toString() : undefined,
                name: props.data.name,
                tableDate: props.data.tableDate,
                rows: props.data.rows,
                columns: props.data.columns.map((element, index) => {
                    return {
                        ...element,
                        columnId: element.id ? parseInt(element.id) : index
                    }
                }),
            })
    }, [props.data])

    function removeAllCells(index: number): void {
        fieldsRows.forEach((e, indexRow) => {
            updateRows(indexRow, {
                ...e, cells: e.cells?.filter((_e, indexCell) => indexCell !== index)
            })
        })
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(props.submit)}>
                <div className='box-real-estate-date'>
                    <div className="box-controller">
                        <Controller
                            name="name"
                            defaultValue={''}
                            control={control}
                            render={({ field: { value, onChange } }) => {
                                return (
                                    <InputForm
                                        label="Nome da tabela"
                                        placeholder='Nome da tabela'
                                        name="name"
                                        value={value}
                                        onChange={onChange}
                                        style={{ width: '15rem' }}
                                        size='small'
                                    />
                                )
                            }}
                        />
                        <ErrorForm error={methods.formState.errors.name?.message} />
                    </div>
                    <div className="box-controller">
                        <Controller
                            name='realEstateId'
                            control={methods.control}
                            defaultValue={'none'}
                            render={({ field: { value, onChange } }) => {
                                return (
                                    <CustomSelect
                                        label='Imóvel'
                                        placeholder='Selecione...'
                                        name='realEstateId'
                                        value={value}
                                        defaultValue={value}
                                        onChange={onChange}
                                        style={{ width: '15rem' }}
                                        options={realEstates}
                                        isFirstNoneDefault
                                    />
                                )
                            }}
                        />
                        <ErrorForm error={methods.formState.errors.realEstateId?.message} />
                    </div>
                    <div className="box-controller">
                        <Controller
                            name='tableDate'
                            control={methods.control}
                            defaultValue={new Date().toString()}
                            render={({ field: { value, onChange } }) => {
                                return (
                                    <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                        adapterLocale={ptBr}
                                        localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
                                    >
                                        <MobileDatePicker
                                            views={['year', 'month']}
                                            value={value}
                                            onChange={onChange}
                                            inputFormat='MM/YYYY'
                                            mask='____/__'
                                            renderInput={(params) => <InputForm name='tableDate' size='small' {...params} label={'Data da Tabela'} />}
                                            toolbarTitle={'SELECIONE A DATA'}
                                        />
                                    </LocalizationProvider>
                                )
                            }}
                        />
                    </div>
                    <div className='hbs-box'>
                        <h6 className='input-title'>Upload template hbs</h6>
                        <InputTemplate
                            name='file'
                            acceptFiles='.hbs'
                            onChange={(value) => methods.setValue('file', value)}
                            getTemplate={props.getTemplate}
                            removeTemplate={(remove) => methods.setValue('removeTemplate', remove)}
                            hasTemplate={props.data?.hasTemplate}
                            style={{ width: '10rem', height: '10rem' }}
                            params={props.params}
                        />
                    </div>
                </div>
                <div className='box-table-sales'>
                    <div className='table-sales-form'>
                        <table className='table-sales'>
                            <thead className='thead'>
                                <TableSaleHeader
                                    columns={fieldsColumns}
                                    append={appendColumns}
                                    removeColumn={(index) => removeColumns(index)}
                                    removeCells={index => removeAllCells(index)}
                                />
                            </thead>
                            <tbody className='tbody'>
                                <TableSaleBody
                                    columns={fieldsColumns}
                                    rows={fieldsRows}
                                    remove={(index) => removeRows(index)}
                                />
                            </tbody>
                        </table>
                        <div className='box-add-row'>
                            <div className='box-add-row-button'>
                                <Button
                                    type='button'
                                    onClick={() => appendRows({
                                        cells: fieldsColumns.map((c, i) => ({ value: '', columnId: c.columnId ? c.columnId : i }))
                                    })}
                                    placeholder='Nova Linha'
                                    title='Adicionar Linha'
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='box-buttons buttons-table-sales'>
                    <Button type="button" secondary placeholder={'Cancelar'} onClick={() => navigate(-1)} />
                    <Button type="submit" placeholder={'Salvar'} />
                </div>
            </form >
        </FormProvider>
    )
}
